import {
	type IdleActivityStoreModel,
	IdleTimeoutController,
	type IdleTimeoutSource,
	isRealPointerMove,
	planIdleTracking,
} from '@supply-mover-2-0/core';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseIdleTimeoutOptions {
	/** Only a signed-in user is timed. */
	isAuthenticated: boolean;
	/**
	 * The tenant's idle limit for this app. `undefined`: not loaded yet, do
	 * nothing. `null`: the tenant has idle sign-out off.
	 */
	limitMinutes: number | null | undefined;
	/** The app's key from `IDLE_ACTIVITY_STORAGE_KEYS`. */
	storageKey: string;
	/**
	 * Sign the user out. `local`: this tab reached the limit and signs out,
	 * revoke included. `remote`: another tab's sign-out was heard first;
	 * follow it without revoking. (Two tabs reaching the limit in the same
	 * instant both get `local`; see IdleTimeoutController._timeOut.)
	 */
	onTimeout: (source: IdleTimeoutSource) => void;
	/**
	 * Something other than input means the user is there (the CRM passes
	 * "a softphone call is in progress"). While true there is no warning and
	 * no sign-out; when it turns false the clock restarts from zero.
	 */
	hold?: boolean;
}

export interface UseIdleTimeoutResult {
	/** The warning dialog should be showing. */
	warningVisible: boolean;
	/** Whole seconds until the sign-out while the warning shows. */
	secondsRemaining: number;
	/** "Stay signed in": counts as activity and closes the warning. */
	staySignedIn: () => void;
}

/**
 * Input that proves someone is at the device. Deliberately absent: `scroll`
 * (it also fires for scrolling done by code, such as a list keeping its
 * place, and every real scroll already arrives as wheel, touch, key or
 * pointer input) and anything network-related (API calls and background
 * polling run whether or not anyone is there).
 *
 * All of these are passive: they keep the session alive but never answer
 * the warning. A press on the warning answers it through the dialog itself
 * (its button, or a click outside it, which lands on the dialog's own
 * backdrop rather than the page). Closing the dialog on the pointerdown would
 * let the click that follows fall through to the page behind it.
 */
const PASSIVE_EVENTS = [
	'pointerdown',
	'wheel',
	'touchstart',
	'touchmove',
] as const;

/** Pointer movement, checked against the last position (see isRealPointerMove). */
const MOVE_EVENTS = ['pointermove', 'mousemove'] as const;

function localStorageStore(key: string): IdleActivityStoreModel {
	return {
		read: (): string | null => window.localStorage.getItem(key),
		write: (value: string): void => {
			window.localStorage.setItem(key, value);
		},
	};
}

function removeRecord(key: string): void {
	try {
		window.localStorage.removeItem(key);
	} catch {
		// Storage unavailable: there is no record to leave behind either.
	}
}

/**
 * PD-16414 — idle sign-out for a signed-in session, shared by the CRM and
 * the ecommerce storefront.
 *
 * Wires real user input, tab visibility and the cross-tab `storage` event
 * into the platform-agnostic `IdleTimeoutController` from libs/core, and
 * exposes the warning state for `IdleTimeoutGuard` to render. With the
 * setting off the hook listens to nothing and shows nothing.
 */
export function useIdleTimeout({
	isAuthenticated,
	limitMinutes,
	storageKey,
	onTimeout,
	hold = false,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
	const [deadlineAt, setDeadlineAt] = useState<number | null>(null);
	const [now, setNow] = useState(() => Date.now());
	const controllerRef = useRef<IdleTimeoutController | null>(null);
	// Read synchronously by the keydown listener, which must decide whether
	// to consume the key before React has re-rendered.
	const warningVisibleRef = useRef(false);
	// The latest callback without restarting the clock when its identity
	// changes on a parent re-render.
	const onTimeoutRef = useRef(onTimeout);
	onTimeoutRef.current = onTimeout;
	// Read when a controller is created, so a hold already in place (a call
	// that started before the setting loaded) applies from the first moment.
	const holdRef = useRef(hold);
	holdRef.current = hold;

	const plan = planIdleTracking(isAuthenticated, limitMinutes);
	const mode = plan.mode;
	const limitMs = plan.mode === 'track' ? plan.limitMs : null;

	useEffect(() => {
		if (mode === 'off') {
			// Off for this tenant: forget any activity recorded while it was
			// on, so turning it back on later starts a fresh clock.
			removeRecord(storageKey);
			return undefined;
		}
		if (mode !== 'track' || limitMs == null) {
			return undefined;
		}

		const controller = new IdleTimeoutController({
			limitMs,
			store: localStorageStore(storageKey),
			onWarning: (deadline): void => {
				warningVisibleRef.current = true;
				setNow(Date.now());
				setDeadlineAt(deadline);
			},
			onActive: (): void => {
				warningVisibleRef.current = false;
				setDeadlineAt(null);
			},
			onTimeout: (source): void => {
				warningVisibleRef.current = false;
				setDeadlineAt(null);
				onTimeoutRef.current(source);
			},
		});
		controllerRef.current = controller;
		controller.setHold(holdRef.current);

		let lastPointer: { x: number; y: number } | null = null;
		const onPassive = (): void => {
			controller.recordActivity('passive');
		};
		const onMove = (event: Event): void => {
			const { screenX, screenY } = event as MouseEvent;
			const position = { x: screenX, y: screenY };
			if (!isRealPointerMove(lastPointer, position)) {
				return;
			}
			lastPointer = position;
			controller.recordActivity('passive');
		};
		// A keypress answers the warning. While it shows, the key is consumed
		// so it cannot reach the page (or a shortcut) underneath the dialog.
		const onKey = (event: KeyboardEvent): void => {
			if (warningVisibleRef.current) {
				event.preventDefault();
				event.stopPropagation();
				controller.recordActivity('deliberate');
				return;
			}
			controller.recordActivity('passive');
		};
		// Coming back to the tab or the app counts as activity, but only
		// after the deadline check inside recordActivity: returning after the
		// limit signs out rather than extending.
		const onVisibility = (): void => {
			if (document.visibilityState === 'visible') {
				controller.recordActivity('passive');
			}
		};
		const onStorage = (event: StorageEvent): void => {
			if (event.key === storageKey) {
				controller.handleStorageChange(event.newValue);
			}
		};

		for (const type of PASSIVE_EVENTS) {
			window.addEventListener(type, onPassive, {
				capture: true,
				passive: true,
			});
		}
		for (const type of MOVE_EVENTS) {
			window.addEventListener(type, onMove, { capture: true, passive: true });
		}
		// Not passive: it may need to cancel the key (see onKey).
		window.addEventListener('keydown', onKey, { capture: true });
		document.addEventListener('visibilitychange', onVisibility);
		window.addEventListener('focus', onPassive);
		window.addEventListener('storage', onStorage);

		controller.start();

		return (): void => {
			for (const type of PASSIVE_EVENTS) {
				window.removeEventListener(type, onPassive, { capture: true });
			}
			for (const type of MOVE_EVENTS) {
				window.removeEventListener(type, onMove, { capture: true });
			}
			window.removeEventListener('keydown', onKey, { capture: true });
			document.removeEventListener('visibilitychange', onVisibility);
			window.removeEventListener('focus', onPassive);
			window.removeEventListener('storage', onStorage);
			controller.stop();
			controllerRef.current = null;
			warningVisibleRef.current = false;
			setDeadlineAt(null);
		};
	}, [mode, limitMs, storageKey]);

	useEffect(() => {
		controllerRef.current?.setHold(hold);
	}, [hold]);

	// Tick the countdown only while the warning shows.
	useEffect(() => {
		if (deadlineAt == null) {
			return undefined;
		}
		const interval = window.setInterval(() => {
			setNow(Date.now());
		}, 1000);
		return (): void => {
			window.clearInterval(interval);
		};
	}, [deadlineAt]);

	const staySignedIn = useCallback((): void => {
		controllerRef.current?.recordActivity('deliberate');
	}, []);

	return {
		warningVisible: deadlineAt != null,
		secondsRemaining:
			deadlineAt == null
				? 0
				: Math.max(0, Math.ceil((deadlineAt - now) / 1000)),
		staySignedIn,
	};
}
